import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useAttachPayDataInvoiceMutation,
  useListPayDataInvoicesQuery,
  type AttachInvoiceRow,
  type PayDataInvoiceItem,
} from '@/store/api/payDataInvoiceApi'
import type { PayDataListItem } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  contractNo: string
  paydataIds: number[]
  rows: PayDataListItem[]
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

// --------------------------------------------------------------------------- //
// Numeric helpers (Standard: thousand sep + 2 decimals, right-aligned).        //
// --------------------------------------------------------------------------- //
function parseNumeric(v: string | number | null | undefined): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Format numeric for read-only display. */
const fmtNum = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : formatStandard(value)

/** Format date DD-MMM-YYYY (Access display d-mmm-yy). */
const fmtDate = (value: string | null | undefined): string => {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('DD-MMM-YYYY') : ''
}

/** Autocomplete label cho invoice option: "<invoice_no> — <date> — <amount>". */
const invoiceLabel = (it: PayDataInvoiceItem | null): string => {
  if (!it) return ''
  const parts = [it.invoice_no ?? '']
  const d = fmtDate(it.invoice_date)
  if (d) parts.push(d)
  if (it.total_payment != null) parts.push(fmtNum(it.total_payment))
  return parts.filter(Boolean).join(' — ')
}

const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

/** Per-row edit draft (mirror Access subFormEdit editable columns). */
interface RowDraft {
  invoice_no: string
  paid_usd: string
  fx: string
  vat_amount_vnd: string
  paid_vnd: string
}

const EDIT_KEYS: (keyof RowDraft)[] = ['paid_usd', 'fx', 'vat_amount_vnd', 'paid_vnd']

/**
 * Pay Data → Add Invoice dialog — converts Access
 * `frmProcurementQryPayDataInvoiceAdd` (+ subforms `frmProcurementQryPayDataInvoiceRef`
 * & `frmProcurementQryPayDataInvoice`).
 *
 * Opened from the Pay Data list for ticked rows of ONE contract. Three parts:
 *   1. General Information (Ref) — read-only summary (DSum over ticked paydata).
 *   2. Invoice (Ref) — read-only grid of ALL invoices for the contract.
 *   3. Edit grid — one row per ticked paydata; each row picks its own invoice_no
 *      and edits paid_usd / fx / vat_amount_vnd / paid_vnd.
 *
 * Save mirrors Access `btnSave_Click`: per row → UPDATE paydata SET invoice_no,
 * paid_usd, paid_vnd, vat_amount_vnd, fx.
 */
export default function PayDataAddInvoiceDialog({
  open,
  contractNo,
  paydataIds,
  rows,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  const { data, isFetching, isError, error } = useListPayDataInvoicesQuery(
    { contractNo, paydataIds },
    { skip: !open || !contractNo, refetchOnMountOrArgChange: true },
  )
  const [attach, { isLoading: isSaving }] = useAttachPayDataInvoiceMutation()

  // Per-row edit drafts keyed by paydata_p_id.
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})

  // Re-init each time the dialog opens (hydrate from ticked rows).
  useEffect(() => {
    if (!open) return
    const next: Record<number, RowDraft> = {}
    for (const r of rows) {
      next[r.paydata_p_id] = {
        invoice_no: String(r.invoice_no ?? ''),
        paid_usd: formatStandard(r.paid_usd),
        fx: formatStandard(r.fx),
        vat_amount_vnd: formatStandard(r.vat_amount_vnd),
        paid_vnd: formatStandard(r.paid_vnd),
      }
    }
    setDrafts(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (isError) snack.error(getErrorMessage(error, 'Failed to load invoices'))
  }, [isError, error, snack])

  const summary = data?.summary
  const items = useMemo(() => data?.items ?? [], [data])

  // Total footer sums (Access AggregateType=0 columns only).
  const refTotals = useMemo(() => {
    const a = { quantity: 0, price_vnd_kg: 0, total_payment: 0, vat_amount: 0, total_amount_after_tax: 0 }
    for (const it of items) {
      a.quantity += it.quantity ?? 0
      a.price_vnd_kg += it.price_vnd_kg ?? 0
      a.total_payment += it.total_payment ?? 0
      a.vat_amount += it.vat_amount ?? 0
      a.total_amount_after_tax += it.total_amount_after_tax ?? 0
    }
    return a
  }, [items])

  const editTotals = useMemo(() => {
    const a = { requested_usd: 0, paid_usd: 0, requested_vnd: 0, vat_amount_vnd: 0, paid_vnd: 0, quantity: 0 }
    for (const r of rows) {
      const d = drafts[r.paydata_p_id]
      a.requested_usd += r.requested_usd ?? 0
      a.requested_vnd += r.requested_vnd ?? 0
      a.quantity += r.quantity ?? 0
      a.paid_usd += parseNumeric(d?.paid_usd) ?? 0
      a.vat_amount_vnd += parseNumeric(d?.vat_amount_vnd) ?? 0
      a.paid_vnd += parseNumeric(d?.paid_vnd) ?? 0
    }
    return a
  }, [rows, drafts])

  const setCell = (pid: number, key: keyof RowDraft, value: string) =>
    setDrafts((prev) => ({ ...prev, [pid]: { ...prev[pid], [key]: value } }))

  const blurCell = (pid: number, key: keyof RowDraft) => () =>
    setDrafts((prev) => {
      const cur = prev[pid]
      if (!cur) return prev
      const formatted = formatStandard(cur[key])
      if (formatted === cur[key]) return prev
      return { ...prev, [pid]: { ...cur, [key]: formatted } }
    })

  const savingRef = useRef(false)
  const handleSave = async () => {
    if (!canEdit || isSaving || isFetching || savingRef.current) return
    savingRef.current = true
    try {
      const payloadRows: AttachInvoiceRow[] = rows.map((r) => {
        const d = drafts[r.paydata_p_id]
        return {
          paydata_p_id: r.paydata_p_id,
          invoice_no: d?.invoice_no?.trim() ? d.invoice_no.trim() : null,
          paid_usd: parseNumeric(d?.paid_usd),
          fx: parseNumeric(d?.fx),
          vat_amount_vnd: parseNumeric(d?.vat_amount_vnd),
          paid_vnd: parseNumeric(d?.paid_vnd),
        }
      })
      await attach({ contract_no: contractNo, rows: payloadRows }).unwrap()
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to attach invoice'))
    } finally {
      savingRef.current = false
    }
  }

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  // General Info (Ref) read-only field.
  const roField = (label: string, value: string, numeric = false, sx?: SxProps<Theme>) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={numeric ? numericInputProps : undefined}
      sx={[roSx, ...(numeric ? [numSx] : []), ...(sx ? [sx] : [])] as SxProps<Theme>}
    />
  )

  // Head cells for the two grids (label + numeric alignment).
  const refHead: { label: string; num?: boolean }[] = [
    { label: 'Invoice ID', num: true },
    { label: 'Warehouse' },
    { label: 'ContractNo' },
    { label: 'Invoice No' },
    { label: 'Invoice Date' },
    { label: 'Quantity', num: true },
    { label: 'Price (VND/KG)', num: true },
    { label: 'Total Amount', num: true },
    { label: 'VAT Amount', num: true },
    { label: 'Total Amount (after tax)', num: true },
  ]

  const cellSx = { py: 0.5, px: 1, whiteSpace: 'nowrap' as const, fontSize: 13 }
  const editInputSx = [numSx, { minWidth: 110 }] as SxProps<Theme>
  // Sticky Total footer cell (stays visible while scrolling).
  const footCellSx = {
    ...cellSx,
    fontWeight: 700,
    position: 'sticky' as const,
    bottom: 0,
    bgcolor: 'background.paper',
    borderTop: (t: Theme) => `2px solid ${t.palette.divider}`,
    zIndex: 2,
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (isSaving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="Procurement - PayData" sx={dialogTitleTextSx}>
          Inbound Logistics - Add Invoice
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={isSaving || isFetching || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for Pay Data.
          </Alert>
        )}

        {/* ── Part 1 — General Information (Ref) ─────────────────────────── */}
        <Typography sx={sectionHeaderSx}>General Information (Ref)</Typography>
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
            mb: 2,
          }}
        >
          {roField('P - Contract', summary?.contract_no ?? contractNo)}
          {roField('P - Price Unit', summary?.price_unit ?? '')}
          {roField('P - Contract Type', summary?.contract_type ?? '')}
          {roField('Supplier', summary?.supplier ?? '')}
          {roField('Request USD (Total)', fmtNum(summary?.requested_usd), true)}
          {roField('Requested VND (Total)', fmtNum(summary?.requested_vnd), true)}
          {roField('Quantity (kg) (Total)', fmtNum(summary?.quantity), true)}
          {roField('Advanced (Total)', fmtNum(summary?.advanced), true)}
        </Box>

        {/* ── Part 2 — Edit grid (one row per ticked paydata) ────────────── */}
        <Typography sx={sectionHeaderSx}>
          Add Invoice (Edit - Please check the statistics when adding Invoice to Paydata)
        </Typography>
        <Box className="app-dataframe" sx={{ height: '280px !important', mb: 2 }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  {[
                    { label: 'Pay ID', num: true },
                    { label: 'Invoice No' },
                    { label: 'Requested USD', num: true },
                    { label: 'Paid USD', num: true },
                    { label: 'Paid FX', num: true },
                    { label: 'Requested VND', num: true },
                    { label: 'VAT', num: true },
                    { label: 'Paid VND', num: true },
                    { label: 'Request - Quantity', num: true },
                    { label: 'Request - Price', num: true },
                  ].map((h) => (
                    <TableCell
                      key={h.label}
                      align={h.num ? 'right' : 'left'}
                      className="app-table-header-cell"
                      sx={cellSx}
                    >
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
              {rows.map((r) => {
                const d = drafts[r.paydata_p_id]
                const selInv =
                  items.find((it) => it.invoice_no === d?.invoice_no) ??
                  (d?.invoice_no ? { invoice_id: -1, invoice_no: d.invoice_no } as PayDataInvoiceItem : null)
                const mergedInv =
                  selInv && !items.some((it) => it.invoice_no === selInv.invoice_no)
                    ? [selInv, ...items]
                    : items
                return (
                  <TableRow key={r.paydata_p_id}>
                    <TableCell align="right" sx={cellSx}>{r.paydata_p_id}</TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 260 }}>
                      <Autocomplete
                        size="small"
                        options={mergedInv}
                        value={selInv}
                        disabled={!canEdit}
                        onChange={(_e, v) => setCell(r.paydata_p_id, 'invoice_no', v?.invoice_no ?? '')}
                        // Field chỉ hiển thị invoice_no; dropdown hiện full label.
                        getOptionLabel={(o) => o?.invoice_no ?? ''}
                        renderOption={(props, o) => (
                          <li {...props} key={o.invoice_id}>
                            {invoiceLabel(o)}
                          </li>
                        )}
                        isOptionEqualToValue={(a, b) => a.invoice_no === b.invoice_no}
                        slotProps={{ popper: { style: { width: 'fit-content', minWidth: 360 } } }}
                        renderInput={(p) => <TextField {...p} label="Invoice No" />}
                      />
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>{fmtNum(r.requested_usd)}</TableCell>
                    {EDIT_KEYS.slice(0, 2).map((k) => (
                      <TableCell key={k} sx={cellSx}>
                        <TextField
                          size="small"
                          type="text"
                          value={d?.[k] ?? ''}
                          disabled={!canEdit}
                          onChange={(e) => setCell(r.paydata_p_id, k, e.target.value)}
                          onBlur={blurCell(r.paydata_p_id, k)}
                          inputProps={numericInputProps}
                          sx={editInputSx}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={cellSx}>{fmtNum(r.requested_vnd)}</TableCell>
                    {EDIT_KEYS.slice(2).map((k) => (
                      <TableCell key={k} sx={cellSx}>
                        <TextField
                          size="small"
                          type="text"
                          value={d?.[k] ?? ''}
                          disabled={!canEdit}
                          onChange={(e) => setCell(r.paydata_p_id, k, e.target.value)}
                          onBlur={blurCell(r.paydata_p_id, k)}
                          inputProps={numericInputProps}
                          sx={editInputSx}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={cellSx}>{fmtNum(r.quantity)}</TableCell>
                    <TableCell align="right" sx={cellSx}>{fmtNum(r.price)}</TableCell>
                  </TableRow>
                )
              })}
              {rows.length > 0 && (
                <TableRow>
                  <TableCell sx={footCellSx}>Total</TableCell>
                  <TableCell sx={footCellSx} />
                  <TableCell align="right" sx={footCellSx}>{fmtNum(editTotals.requested_usd)}</TableCell>
                  <TableCell align="right" sx={footCellSx}>{fmtNum(editTotals.paid_usd)}</TableCell>
                  <TableCell sx={footCellSx} />
                  <TableCell align="right" sx={footCellSx}>{fmtNum(editTotals.requested_vnd)}</TableCell>
                  <TableCell align="right" sx={footCellSx}>{fmtNum(editTotals.vat_amount_vnd)}</TableCell>
                  <TableCell align="right" sx={footCellSx}>{fmtNum(editTotals.paid_vnd)}</TableCell>
                  <TableCell align="right" sx={footCellSx}>{fmtNum(editTotals.quantity)}</TableCell>
                  <TableCell sx={footCellSx} />
                </TableRow>
              )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* ── Part 3 — Invoice (Ref) read-only grid ──────────────────────── */}
        <Typography sx={sectionHeaderSx}>Invoice (Ref)</Typography>
        <Box className="app-dataframe" sx={{ height: '220px !important', mb: 2 }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {refHead.map((h) => (
                    <TableCell
                      key={h.label}
                      align={h.num ? 'right' : 'left'}
                      className="app-table-header-cell"
                      sx={cellSx}
                    >
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={refHead.length} align="center" sx={cellSx}>
                      <CircularProgress size={18} />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={refHead.length} align="center" sx={{ ...cellSx, color: 'text.secondary' }}>
                      No invoices for {contractNo}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((it) => (
                    <TableRow key={it.invoice_id} hover>
                      <TableCell align="right" sx={cellSx}>{it.invoice_id}</TableCell>
                      <TableCell sx={cellSx}>{it.warehouse ?? ''}</TableCell>
                      <TableCell sx={cellSx}>{it.contract_no ?? ''}</TableCell>
                      <TableCell sx={cellSx}>{it.invoice_no ?? ''}</TableCell>
                      <TableCell sx={cellSx}>{fmtDate(it.invoice_date)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmtNum(it.quantity)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmtNum(it.price_vnd_kg)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmtNum(it.total_payment)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmtNum(it.vat_amount)}</TableCell>
                      <TableCell align="right" sx={cellSx}>{fmtNum(it.total_amount_after_tax)}</TableCell>
                    </TableRow>
                  ))
                )}
                {!isFetching && items.length > 0 && (
                  <TableRow>
                    <TableCell sx={footCellSx}>Total</TableCell>
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell sx={footCellSx} />
                    <TableCell align="right" sx={footCellSx}>{fmtNum(refTotals.quantity)}</TableCell>
                    <TableCell align="right" sx={footCellSx}>{fmtNum(refTotals.price_vnd_kg)}</TableCell>
                    <TableCell align="right" sx={footCellSx}>{fmtNum(refTotals.total_payment)}</TableCell>
                    <TableCell align="right" sx={footCellSx}>{fmtNum(refTotals.vat_amount)}</TableCell>
                    <TableCell align="right" sx={footCellSx}>{fmtNum(refTotals.total_amount_after_tax)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {isFetching
            ? 'Loading invoices…'
            : `${items.length} invoice(s) for ${contractNo} · ${rows.length} Pay Data row(s) selected`}
        </Typography>
      </DialogContent>
    </Dialog>
  )
}
